import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatCurrency, formatMXN, convert } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CurrencyContext = createContext(null);
export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const { data: ratesData } = useQuery({
    queryKey: ["rates"],
    queryFn: async () => (await api.get("/rates")).data,
    staleTime: 3600000,
  });
  const rates = ratesData?.rates;
  const ratesDate = ratesData?.date;
  const ratesSource = ratesData?.source;
  const approx = ratesData?.approx || [];
  const enabled = user?.currencies?.length ? user.currencies : ["MXN"];
  const [currency, setCurrencyState] = useState("MXN");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("byto_currency");
    if (saved && enabled.includes(saved)) {
      setCurrencyState(saved);
    } else if (!touched && user?.default_currency && enabled.includes(user.default_currency)) {
      setCurrencyState(user.default_currency);
    } else if (!enabled.includes(currency)) {
      setCurrencyState(enabled[0] || "MXN");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.default_currency, JSON.stringify(enabled)]);

  const setCurrency = (c) => { setCurrencyState(c); setTouched(true); localStorage.setItem("byto_currency", c); };

  const format = (mxn) => formatCurrency(mxn, currency, rates);
  const compact = (mxn) => {
    const val = convert(mxn, currency, rates);
    let sym = "$";
    try { sym = new Intl.NumberFormat("en", { style: "currency", currency }).format(0).replace(/[\d.,\s]/g, "") || "$"; } catch (e) {}
    return sym + new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(val || 0);
  };

  const rateFor = (code) => (code === "MXN" ? 1 : (rates ? rates[code] : null));

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, enabled, format, compact, formatMXN, ratesDate, ratesSource, approx, rateFor }}>
      {children}
    </CurrencyContext.Provider>
  );
};
